import React, { useEffect, useState } from 'react';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faClock, faCheck, faTimes, faStore, faTags } from '@fortawesome/free-solid-svg-icons';
import { getLabelColor } from '../../utils/labelColors';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  UiPageHeader, UiButton, UiInput, StatusBadge, FIELD_INPUT_CLASS, FIELD_LABEL_CLASS,
} from '../../components/ui';
import type { StatusDef } from '../../components/ui';

interface MerchantRequest {
  id: string;
  merchant_name: string;
  company_name: string;
  email: string;
  phone: string;
  fee_percentage: number;
  payout_fee_percentage: number;
  reseller_labels: string[];
  status: string;
  admin_note: string;
  created_at: string;
}

const REQUEST_STATUS_MAP: Record<string, StatusDef> = {
  pending: { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-400', icon: faClock, label: 'Pending' },
  approved: { bg: 'bg-green-100 dark:bg-green-500/15', text: 'text-green-700 dark:text-green-400', icon: faCheck, label: 'Approved' },
  rejected: { bg: 'bg-red-100 dark:bg-red-500/15', text: 'text-red-700 dark:text-red-400', icon: faTimes, label: 'Rejected' },
};

const REQUEST_COLUMNS: Column<MerchantRequest>[] = [
  {
    key: 'merchant',
    header: 'Merchant',
    render: (req) => (
      <div>
        <p className="font-medium text-gray-800 dark:text-slate-100">{req.merchant_name}</p>
        {req.company_name && <p className="text-xs text-gray-400 dark:text-slate-500">{req.company_name}</p>}
      </div>
    ),
  },
  {
    key: 'email',
    header: 'Email',
    render: (req) => <span className="text-sm text-gray-600 dark:text-slate-300">{req.email}</span>,
  },
  {
    key: 'fee',
    header: 'Fee %',
    headerClassName: 'text-center',
    className: 'text-center',
    render: (req) => (
      <span className="text-sm font-medium tabular-nums text-gray-800 dark:text-slate-100">{req.fee_percentage}%</span>
    ),
  },
  {
    key: 'payout_fee',
    header: 'Payout Fee %',
    headerClassName: 'text-center',
    className: 'text-center',
    render: (req) => (
      <span className="text-sm font-medium tabular-nums" style={{ color: 'var(--theme-accent)' }}>
        {req.payout_fee_percentage || 0}%
      </span>
    ),
  },
  {
    key: 'labels',
    header: 'Labels',
    render: (req) => (
      <div className="flex flex-wrap gap-1">
        {(req.reseller_labels || []).length > 0
          ? (req.reseller_labels || []).map(label => (
              <span key={label} className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getLabelColor(label)}`}>
                {label}
              </span>
            ))
          : <span className="text-gray-300 dark:text-slate-600 text-xs">-</span>
        }
      </div>
    ),
  },
  {
    key: 'status',
    header: 'Status',
    headerClassName: 'text-center',
    className: 'text-center',
    render: (req) => <StatusBadge status={req.status} statusMap={REQUEST_STATUS_MAP} />,
  },
  {
    key: 'note',
    header: 'Note',
    render: (req) => <span className="text-sm text-gray-500 dark:text-slate-400">{req.admin_note || '-'}</span>,
  },
  {
    key: 'date',
    header: 'Date',
    render: (req) => (
      <span className="text-sm text-gray-400 dark:text-slate-500">
        {new Date(req.created_at).toLocaleDateString('th-TH')}
      </span>
    ),
  },
];

const MerchantRequestPage: React.FC = () => {
  const [requests, setRequests] = useState<MerchantRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const resellerUser = JSON.parse(localStorage.getItem('reseller_user') || '{}');

  const [form, setForm] = useState({
    merchant_name: '',
    company_name: '',
    email: '',
    phone: '',
    fee_percentage: '',
    payout_fee_percentage: ''
  });

  const [formLabels, setFormLabels] = useState<string[]>([]);
  const [labelInput, setLabelInput] = useState('');

  useEffect(() => {
    loadRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadRequests = async () => {
    try {
      const response = await merchantApi.get('/reseller/merchant-requests');
      setRequests(response.data.data || []);
    } catch (error: any) {
      // เคลียร์ของเก่า ไม่ให้รายการคำขอค้างเมื่อโหลดพลาด
      console.error('Error loading requests:', error);
      setRequests([]);
      toast.error('โหลดรายการคำขอไม่สำเร็จ: ' + (error?.response?.data?.message || error?.message || ''));
    } finally {
      setLoading(false);
    }
  };

  const addLabel = () => {
    const trimmed = labelInput.trim();
    if (!trimmed || formLabels.includes(trimmed) || formLabels.length >= 10) return;
    setFormLabels([...formLabels, trimmed]);
    setLabelInput('');
  };

  const removeLabel = (label: string) => setFormLabels(formLabels.filter(l => l !== label));

  const existingLabels = Array.from(new Set(
    requests.flatMap(r => r.reseller_labels || [])
  )).filter(l => !formLabels.includes(l)).sort();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const minPayinRate = parseFloat(resellerUser.fee_rate || 0);
    const minPayoutRate = parseFloat(resellerUser.payout_fee_rate || 0);
    const feeVal = parseFloat(form.fee_percentage);
    const payoutFeeVal = parseFloat(form.payout_fee_percentage || '0');

    if (feeVal < minPayinRate) {
      toast.error(`Payin Fee ต้องไม่น้อยกว่า ${minPayinRate}% (rate ของคุณ)`);
      return;
    }
    if (payoutFeeVal < minPayoutRate) {
      toast.error(`Payout Fee ต้องไม่น้อยกว่า ${minPayoutRate}% (payout rate ของคุณ)`);
      return;
    }

    setSubmitting(true);
    try {
      await merchantApi.post('/reseller/merchant-requests', {
        ...form,
        fee_percentage: feeVal,
        payout_fee_percentage: payoutFeeVal,
        labels: formLabels
      });
      toast.success('Submitted request successfully!');
      setShowForm(false);
      setForm({ merchant_name: '', company_name: '', email: '', phone: '', fee_percentage: '', payout_fee_percentage: '' });
      setFormLabels([]);
      setLabelInput('');
      loadRequests();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Error submitting request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faStore}
        title="Open Merchant"
        subtitle="Submit merchant opening requests"
        actions={
          <UiButton icon={faPlus} onClick={() => setShowForm(!showForm)}>
            New Request
          </UiButton>
        }
      />

      {/* Form */}
      {showForm && (
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-slate-100 mb-4">New Merchant Request</h2>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <UiInput
              label="Merchant Name"
              required
              type="text"
              value={form.merchant_name}
              onChange={(e) => setForm({ ...form, merchant_name: e.target.value })}
              placeholder="Merchant name"
            />
            <UiInput
              label="Company Name"
              type="text"
              value={form.company_name}
              onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              placeholder="Company name (optional)"
            />
            <UiInput
              label="Email"
              required
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="merchant@example.com"
            />
            <UiInput
              label="Phone"
              type="text"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="Phone (optional)"
            />
            <UiInput
              label={
                <>
                  Fee %{' '}
                  <span className="font-normal normal-case text-gray-400 dark:text-slate-500">(min: {resellerUser.fee_rate || 0}%)</span>
                </>
              }
              required
              type="number"
              step="0.01"
              min={resellerUser.fee_rate || 0}
              value={form.fee_percentage}
              onChange={(e) => setForm({ ...form, fee_percentage: e.target.value })}
              placeholder={`min ${resellerUser.fee_rate || 0}%`}
            />
            <UiInput
              label={
                <>
                  Payout Fee %{' '}
                  <span className="font-normal normal-case text-gray-400 dark:text-slate-500">(min: {resellerUser.payout_fee_rate || 0}%)</span>
                </>
              }
              type="number"
              step="0.01"
              min={resellerUser.payout_fee_rate || 0}
              value={form.payout_fee_percentage}
              onChange={(e) => setForm({ ...form, payout_fee_percentage: e.target.value })}
              placeholder={`min ${resellerUser.payout_fee_rate || 0}%`}
            />

            {/* Labels */}
            <div className="md:col-span-2">
              <label className={FIELD_LABEL_CLASS}>
                <FontAwesomeIcon icon={faTags} className="mr-1.5 text-gray-400 dark:text-slate-500" />
                Labels <span className="font-normal normal-case text-gray-400 dark:text-slate-500">(optional)</span>
              </label>
              <div className="border border-gray-200 dark:border-slate-600 rounded-xl p-3 bg-gray-50 dark:bg-slate-700/50">
                {/* Current labels */}
                <div className="flex flex-wrap gap-1 mb-2 min-h-[24px]">
                  {formLabels.length === 0 && (
                    <span className="text-xs text-gray-300 dark:text-slate-600 italic">ยังไม่มี label</span>
                  )}
                  {formLabels.map(label => (
                    <span key={label} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getLabelColor(label)}`}>
                      {label}
                      <button type="button" onClick={() => removeLabel(label)} className="hover:opacity-70 ml-0.5">
                        <FontAwesomeIcon icon={faTimes} className="text-[10px]" />
                      </button>
                    </span>
                  ))}
                </div>
                {/* Existing labels suggestions */}
                {existingLabels.length > 0 && (
                  <div className="mb-2">
                    <p className="text-[10px] text-gray-400 dark:text-slate-500 mb-1">Labels ที่มีอยู่ — คลิกเพื่อเพิ่ม</p>
                    <div className="flex flex-wrap gap-1">
                      {existingLabels.map(label => (
                        <button
                          key={label}
                          type="button"
                          onClick={() => setFormLabels(prev => [...prev, label])}
                          disabled={formLabels.length >= 10}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border border-dashed opacity-60 hover:opacity-100 transition-opacity disabled:cursor-not-allowed ${getLabelColor(label)}`}
                        >
                          <FontAwesomeIcon icon={faPlus} className="text-[9px]" />{label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Input */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={labelInput}
                    onChange={(e) => setLabelInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addLabel(); } }}
                    placeholder="พิมพ์ label ใหม่ แล้วกด Enter"
                    maxLength={30}
                    className={`flex-1 ${FIELD_INPUT_CLASS}`}
                  />
                  <button
                    type="button"
                    onClick={addLabel}
                    className="px-3 rounded-lg text-sm bg-gray-200 dark:bg-slate-600 text-gray-600 dark:text-slate-300 hover:bg-gray-300 dark:hover:bg-slate-500 transition-colors"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-end gap-3">
              <UiButton type="submit" loading={submitting}>
                {submitting ? 'Submitting...' : 'Submit Request'}
              </UiButton>
              <UiButton variant="secondary" onClick={() => setShowForm(false)}>
                Cancel
              </UiButton>
            </div>
          </form>
        </div>
      )}

      {/* Request List */}
      <DataTable<MerchantRequest>
        data={requests}
        columns={REQUEST_COLUMNS}
        loading={loading}
        emptyMessage="No requests yet"
        keyExtractor={(req) => req.id}
      />
    </div>
  );
};

export default MerchantRequestPage;
